// Version history & restore — where versions come from, how restore behaves,
// retention. Sourced from specs/history.md.

import { IconClockHour4, IconHistory, IconRestore } from "@tabler/icons-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { DocSection, K, P, Points } from "../doc-ui";

export function VersionHistoryDoc() {
  return (
    <>
      <P>
        Every note keeps a version history on the server. Versions are captured automatically as
        you edit — roughly one per edit session (the server snapshots a few seconds after typing
        settles), with no action needed from you.
      </P>

      <DocSection icon={IconClockHour4} title="Browse history">
        <Points>
          <li>
            <strong>Web app</strong> — open a note and click <K>History</K> in the header: a list
            of versions with timestamps, and a preview with a diff against the current text.
          </li>
          <li>
            <strong>Obsidian plugin</strong> — right-click a file → <K>Version history</K> for the
            same list, preview, and diff.
          </li>
        </Points>
      </DocSection>

      <DocSection
        icon={IconRestore}
        title="Restore"
        description="Available to editors and owners; viewers can preview but not restore."
      >
        <P>
          Restoring a version is a normal edit, not a rewind: the old text is applied to the note
          the same way typing would be. That means it merges with anyone else's concurrent edits,
          it syncs to every device, it can be undone, and it itself becomes a new version. Nothing
          in the history is ever destroyed by a restore.
        </P>
        <Alert>
          <IconHistory />
          <AlertTitle>Merged your edits? History has your back</AlertTitle>
          <AlertDescription>
            When two people edit the same note at once, Cairn merges the text instead of creating
            conflict files — and the plugin shows a dismissible notice on the file linking to its
            history, so you can always see and recover exactly what was merged.
          </AlertDescription>
        </Alert>
      </DocSection>

      <DocSection icon={IconClockHour4} title="How long versions are kept">
        <Points>
          <li>
            Note versions are kept for a server-configured retention window (default: one year).
          </li>
          <li>
            Attachments (images, PDFs) that are deleted are garbage-collected sooner (default: 30
            days). Restoring an old version whose attachment is gone still works — the text comes
            back and the missing attachment is marked in the UI.
          </li>
        </Points>
        <P>
          Operators can change both windows in the server config — see{" "}
          <em>Server maintenance</em>.
        </P>
      </DocSection>
    </>
  );
}
